<template>
  <div class="contacts-table" style="height: calc(100vh - 40px - 24px - 34px)">
    <WdsStack direction-column gap-xs>
      <a :href="laravelNamedRoute(namedRoutes.contacts.index)" class="tw-self-start">
        <WdsButtonGhost secondary>
          <WdsStack align-center gap-xs>
            <WdsIcon name="arrow-left" :size="16" />
            {{ $t('Talent pool') }}
          </WdsStack>
        </WdsButtonGhost>
      </a>

      <WdsStack justify-space-between align-center class="tw-mb-7">
        <WdsHeading class="tw-mt-0" type="display">
          {{ appliedCount != null && appliedCount > 0 ? `${appliedCount} ` : '' }}
          {{ $t('Requests to join') }}
        </WdsHeading>

        <WdsButton secondary data-test="invite-link" @click="openInviteLinkModal">
          <WdsStack align-center gap-sm>
            <WdsIcon :size="16" :name="WdsIconNameEnum.LINK" />
            {{ $t('Share link') }}
          </WdsStack>
        </WdsButton>
      </WdsStack>
    </WdsStack>

    <AppliedContactsGraphqlTable
      v-model:total-count="appliedCount"
      @approved="handleApproved"
      @approve-error="handleApproveError"
      @edit="openManageModal"
      @delete="openDeleteModal"
      @block="openBlockModal"
    >
      <template #no-results>
        <WdsStack direction-column align-center justify-center gap-md>
          <WdsText class="tw-text-center tw-pt-5">
            {{
              $t('There are no pending requests from workers who want to join your talent pool.')
            }}
          </WdsText>
          <WdsButton secondary @click="openInviteLinkModal">
            <WdsStack align-center gap-sm>
              <WdsIcon :size="16" :name="WdsIconNameEnum.LINK" />
              {{ $t('Share link') }}
            </WdsStack>
          </WdsButton>
        </WdsStack>
      </template>
    </AppliedContactsGraphqlTable>

    <TrustedContactInviteLink
      :show="inviteLinkModalIsOpen"
      @close="inviteLinkModalIsOpen = false"
    />
    <TrustedContactImportModal :show="importModalIsOpen" @close="closeImportModal" />
    <TrustedContactManageModal
      :show="manageModalIsOpen"
      :account-id="accountId"
      :trusted-contact-id="selectedTrustedContactId"
      data-test="trusted-contact-manage-modal"
      @cancel="closeManageModal"
      @close="closeManageModal"
      @added="refetchContacts"
      @updated="refetchContacts"
    />

    <TrustedContactDeleteModal
      :trusted-contact-id="selectedTrustedContactId"
      :show="deleteModalIsOpen"
      data-test="trusted-contact-delete-modal"
      @cancel="closeDeleteModal"
      @close="closeDeleteModal"
      @deleted="handleDeclined"
    />
    <TrustedContactBlockModal
      :trusted-contact-id="selectedTrustedContactId"
      :show="blockModalIsOpen"
      data-test="trusted-contact-block-modal"
      @cancel="closeBlockModal"
      @close="closeBlockModal"
      @deleted="handleDeclinedAndBlocked"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import {
  WdsStack,
  WdsHeading,
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsText,
  WdsIconNameEnum,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import TrustedContactInviteLink from '@/components/Contacts/CompanyFreelancer/TrustedContactInviteLink.vue'
import TrustedContactImportModal from '@/components/Contacts/CompanyFreelancer/TrustedContactImportModal.vue'
import TrustedContactManageModal from '@/components/Contacts/CompanyFreelancer/TrustedContactManageModal.vue'
import TrustedContactDeleteModal from '@/components/Contacts/CompanyFreelancer/TrustedContactDeleteModal.vue'
import { Message, NetworkError } from '@/utils/notification/index'
import useQueryEncoder from '@/composables/useQueryEncoder'
import AppliedContactsGraphqlTable from '@/components/Contacts/CompanyFreelancer/PageApplicants/AppliedContactsGraphqlTable.vue'
import TrustedContactBlockModal from '@/components/Contacts/CompanyFreelancer/TrustedContactBlockModal.vue'
import { openWorkerDetailPanel } from '@/components/GraphqlDataTable/listners'

const accountId = computed(() => currentAccount.globalId)
const { resolveClient } = useApolloClient()

const inviteLinkModalIsOpen = ref(false)
const importModalIsOpen = ref(false)
const manageModalIsOpen = ref(false)

const deleteModalIsOpen = ref(false)
const blockModalIsOpen = ref(false)
const selectedTrustedContactId = ref(null)
const appliedCount = ref(undefined)

const trustedContactId = useQueryEncoder('id')
const trustedContactWorkerId = useQueryEncoder('worker-id')
const inviteTrustedContact = useQueryEncoder('invite-trusted-contact')

const refetchContacts = () => {
  resolveClient('publicApiCustomFields')?.refetchQueries({
    include: ['AppliedTrustedContactsList'],
  })
}

const openManageModal = (row) => {
  selectedTrustedContactId.value = row.id
  manageModalIsOpen.value = true
}
const closeManageModal = () => {
  manageModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openDeleteModal = (row) => {
  selectedTrustedContactId.value = row.id
  deleteModalIsOpen.value = true
}
const closeDeleteModal = () => {
  deleteModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openBlockModal = (row) => {
  selectedTrustedContactId.value = row.id
  blockModalIsOpen.value = true
}

const closeBlockModal = () => {
  blockModalIsOpen.value = false
  selectedTrustedContactId.value = null
}

const openInviteLinkModal = () => {
  inviteLinkModalIsOpen.value = true
}

const closeImportModal = () => {
  importModalIsOpen.value = false
}

const toggleInviteModal = () => {
  manageModalIsOpen.value = !manageModalIsOpen.value
}

const handleApproved = () => {
  new Message($t('Contact successfully added to your talent pool.')).dispatch()
  refetchContacts()
}

const handleDeclined = () => {
  new Message($t('Contact successfully deleted.')).dispatch()
  refetchContacts()
}

const handleDeclinedAndBlocked = () => {
  new Message($t('Contact successfully declined and blocked.')).dispatch()
  refetchContacts()
}

const handleApproveError = (errors) => {
  new NetworkError({
    msg: errors[0],
    errors,
  }).dispatch()
}

onMounted(() => {
  if (inviteTrustedContact.value) {
    toggleInviteModal()
  }

  if (trustedContactId.value && trustedContactWorkerId.value) {
    openWorkerDetailPanel({
      workerAccountId: trustedContactWorkerId.value,
    })
  }
})
</script>
